import { DatePipe, registerLocaleData } from '@angular/common';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import {
  IonApp,
  IonicRouteStrategy,
  IonRouterOutlet,
  Platform,
  provideIonicAngular,
} from '@ionic/angular';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule, Storage } from '@ionic/storage-angular';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import * as SentryAngular from '@sentry/angular';
import { AccountModule, UsersState } from '@wilson/account';
import { ActivitiesModule } from '@wilson/activities';
import {
  ActivityPropertySettingsState,
  TransportGoodsState,
} from '@wilson/activities/services';
import { ActivityCategoriesState } from '@wilson/activity-categories/data';
import { AuthCoreModule, AuthInterceptor, AuthState } from '@wilson/auth/core';
import { AuthorizationModule, PermissionsState } from '@wilson/authorization';
import { AgreementsState, ClientsState } from '@wilson/clients/states';
import { CompanySettingsCoreModule } from '@wilson/company-settings/core';
import { ConfigModule } from '@wilson/config';
import { ContractsModule } from '@wilson/contracts';
import { FeatureFlagsModule, FeatureFlagsService } from '@wilson/feature-flags';
import { FilesModule } from '@wilson/files';
import { storageAndAuthStateInitializer } from '@wilson/ionic/storage';
import {
  ANGULAR_LOCALE_DATA,
  LocalizationState,
  USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE,
} from '@wilson/localization';
import {
  FavoriteLocationsState,
  LocationSelectionHistoryState,
  LocationSettingsState,
} from '@wilson/locations-v2/state';
import { MasterDataState } from '@wilson/master-data/states';
import { MobileCompanySettingsState } from '@wilson/mobile-company-settings';
import { FavoriteActivityCategoriesState } from '@wilson/non-domain-specific/activities-helpers/states';
import { NotificationsModule } from '@wilson/notifications';
import { OperativeReportCategorySettingsState } from '@wilson/operative-reports/state';
import { OrganizationalUnitModule } from '@wilson/organizational-unit';
import {
  LocationPreferencesState,
  PreferenceSettingsState,
  ShiftTimePreferenceSettingState,
  UserGeneralPreferencesState,
  UserLocationPreferencesState,
  UserShiftTimePreferenceState,
  UserTimePreferencesState,
  UserVehiclePreferencesState,
  VehiclePreferencesState,
} from '@wilson/preferences/state';
import { ProfitabilityScoreState } from '@wilson/profitability-score/state';
import { ProjectsState } from '@wilson/projects/state';
import { QualificationCategoriesModule } from '@wilson/qualification-categories';
import { QualificationSettingsModule } from '@wilson/qualification-settings';
import {
  UserQualificationsV2AttachmentsState,
  UserQualificationsV2State,
} from '@wilson/qualification-v2';
import {
  QualificationsV2CategoriesState,
  QualificationsV2State,
} from '@wilson/qualification-v2/common';
import { QualificationsModule } from '@wilson/qualifications';
import { SectorState } from '@wilson/sectors';
import { ShiftCategoriesState } from '@wilson/shift-category';
import { ShiftsModule } from '@wilson/shifts';
import { ResolvedShiftsState, ShiftsMobileModule } from '@wilson/shifts-mobile';
import { TalkJsConfigToken } from '@wilson/talkjs/services';
import { VehicleClassesState } from '@wilson/vehicle-classes/state';
import * as CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import { MatomoModule, MatomoRouterModule } from 'ngx-matomo-client';
import { withNgxsResetPlugin } from 'ngxs-reset-plugin';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appInitializerFactory } from './app.init';
import { backgroundTaskInitializer } from './initializer/background-task.initializer';
import { JwtOptionsFactory } from './jwt-options.factory';
import { OperativeReportsState } from './modules/operative-reports/state/operative-reports.state';
import { VersionUpdateModule } from './modules/version-update/version-update.module';
import { HomeState } from './pages/home/store/home.state';
import { RequestHeadersInterceptor } from './request-headers.interceptor';
import { CalendarNavigationService } from './services/calendar-navigation.service';
import { CalendarTasksService } from './services/calendar-tasks.service';
import { CalendarService } from './services/calendar.service';
import { MobileLanguagePreferenceStorage } from './services/mobile-language-preference-storage.service';
import { StorageService } from './services/storage.service';
import { DevState } from './store/dev/dev.state';
import { DeviceState } from './store/device/device.state';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';

Object.values(ANGULAR_LOCALE_DATA).forEach((locale) => {
  registerLocaleData(locale);
});
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    IonApp,
    IonRouterOutlet,
    NgxsModule.forRoot(
      [
        AuthState,
        DeviceState,
        HomeState,
        PermissionsState,
        UserGeneralPreferencesState,
        LocalizationState,
        LocationPreferencesState,
        MobileCompanySettingsState,
        MasterDataState,
        ResolvedShiftsState,
        OperativeReportsState,
        OperativeReportCategorySettingsState,
        UserLocationPreferencesState,
        PreferenceSettingsState,
        VehiclePreferencesState,
        UserVehiclePreferencesState,
        UserTimePreferencesState,
        UserShiftTimePreferenceState,
        ShiftTimePreferenceSettingState,
        ShiftCategoriesState,
        LocationSettingsState,
        FavoriteLocationsState,
        ProjectsState,
        ProfitabilityScoreState,
        UsersState,
        FavoriteActivityCategoriesState,
        ActivityCategoriesState,
        LocationSelectionHistoryState,
        DevState,
        UserQualificationsV2State,
        QualificationsV2State,
        QualificationsV2CategoriesState,
        UserQualificationsV2AttachmentsState,
        SectorState,
        ActivityPropertySettingsState,
        TransportGoodsState,
        VehicleClassesState,
        ClientsState,
        AgreementsState,
      ],
      {
        developmentMode: !environment.production,
      },
    ),
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
    NgxsStoragePluginModule.forRoot({
      keys: [
        DeviceState,
        PermissionsState,
        UserQualificationsV2State,
        QualificationsV2State,
        QualificationsV2CategoriesState,
        UserQualificationsV2AttachmentsState,
        HomeState,
        ActivityCategoriesState,
      ],
    }),
    AccountModule,
    ActivitiesModule,
    AppRoutingModule,
    AuthCoreModule,
    AuthorizationModule,
    BrowserModule,
    CompanySettingsCoreModule,
    ContractsModule,
    FilesModule,
    FormsModule,
    ReactiveFormsModule,
    NotificationsModule,
    OrganizationalUnitModule,
    QualificationsModule,
    QualificationCategoriesModule,
    QualificationSettingsModule,
    ShiftsModule,
    ShiftsMobileModule,
    IonicStorageModule.forRoot({
      name: '__mydb',
      driverOrder: [
        CordovaSQLiteDriver._driver,
        Drivers.IndexedDB,
        Drivers.LocalStorage,
      ],
    }),
    ConfigModule.forRoot({
      host: environment.backend.host,
      application: environment.application,
    }),
    FeatureFlagsModule.forRoot({
      clientId: environment.launchDarklyClientId,
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: JwtOptionsFactory,
        deps: [Store],
      },
    }),
    VersionUpdateModule,
    MatomoModule.forRoot({
      disabled: environment.matomo.disabled,
      trackerUrl: environment.matomo.trackerUrl,
      siteId: environment.matomo.siteId,
    }),
    MatomoRouterModule.forRoot({
      exclude: /^\/(?!(tabs\/profile|project))/,
    }),
    IonApp,
    IonRouterOutlet,
  ],
  providers: [
    provideTranslateService({ loader: provideTranslateHttpLoader() }),
    // CDK 21 renders overlays in the browser top layer (native popover) by default, which
    // ignores z-index and changes how ng-zorro overlays stack with other layers. Keep the
    // 20.x behaviour (the CDK already falls back to it where popover isn't supported).
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    DatePipe,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({ swipeBackEnabled: false }),
    CalendarService,
    CalendarTasksService,
    CalendarNavigationService,
    {
      provide: TalkJsConfigToken,
      useValue: environment.talkJsConfig,
    },
    // AuthInterceptor reads the Authorization header JwtInterceptor attaches, so JwtInterceptor
    // must run first: keep JwtModule.forRoot in imports, which registers it ahead of this.
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestHeadersInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: SentryAngular.createErrorHandler(),
    },
    {
      provide: SentryAngular.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const initializerFn = storageAndAuthStateInitializer(
        inject(Storage),
        inject(Store),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = backgroundTaskInitializer(inject(Store));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(
        inject(Store),
        inject(FeatureFlagsService),
        inject(Platform),
        inject(StorageService),
        inject(USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE),
      );
      return initializerFn();
    }),
    {
      provide: USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE,
      useClass: MobileLanguagePreferenceStorage,
    },
    FormBuilder,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    withNgxsResetPlugin(),
    {
      provide: USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE,
      useClass: MobileLanguagePreferenceStorage,
    },
    provideNzDateFnsAdapter(),
  ],
})
export class AppModule {}
